import {
    useSuspenseQuery,
    UseSuspenseQueryResult,
    useMutation,
    UseMutationResult,
    useQueryClient,
    useQuery,
    UseQueryResult,
    useInfiniteQuery,
    UseInfiniteQueryResult,
    InfiniteData,
} from '@tanstack/react-query';
import { API_ROOT } from '@Helpers/apiConfig';
import { createPacerApi } from '@Helpers/pacerApi';
import {
    dashboardFiltersResponseSchema,
    dashboardSettingsResponseSchema,
    overviewTabsConfigResponseSchema,
    saveSettingsResponseSchema,
    checkSettingsAvailabilityResponseSchema,
    locationsByProjectsResponseSchema,
    overviewTabResponseSchema,
    DashboardFiltersResponse,
    DashboardSettingsResponse,
    DashboardSetting,
    OverviewTabsConfigResponse,
    UpdateTabsConfigRequest,
    SetOverviewTabRequest,
    SaveSettingsResponse,
    CheckSettingsAvailabilityResponse,
    LocationsByProjectsResponse,
    OverviewTabResponse,
    OverviewLocationFilters,
    SortByValue,
    SortDirValue,
    locationBreakdownResponseSchema,
    LocationBreakdownResponse,
    breakdownTaskListResponseSchema,
    BreakdownTaskListResponse,
} from './companyDashboard.types';

export type {
    DashboardFiltersResponse,
    DashboardSettingsResponse,
    DashboardSetting,
    OverviewTabsConfigResponse,
    AvailableProject,
    OverviewTabConfig,
    UpdateTabsConfigRequest,
    SetOverviewTabRequest,
    SaveSettingsResponse,
    CheckSettingsAvailabilityResponse,
    LocationsByProjectsResponse,
    OverviewTabResponse,
    LocationSummary,
    PieChartDatum,
    OverviewLocationFilters,
    SortByValue,
    SortDirValue,
    LocationBreakdownResponse,
    LocationBreakdownRow,
    BreakdownTask,
    BreakdownTaskListResponse,
} from './companyDashboard.types';

// ---- Query keys ----

export const DASHBOARD_FILTERS_QUERY_KEY = 'CompanyDashboardFilters';
export const DASHBOARD_SETTINGS_QUERY_KEY = 'CompanyDashboardSettings';
export const OVERVIEW_TABS_CONFIG_QUERY_KEY = 'CompanyDashboardOverviewTabsConfig';
export const LOCATIONS_BY_PROJECTS_QUERY_KEY = 'CompanyDashboardLocationsByProjects';
export const OVERVIEW_TAB_QUERY_KEY = 'CompanyDashboardOverviewTab';
export const LOCATION_BREAKDOWN_QUERY_KEY = 'CompanyDashboardLocationBreakdown';
export const BREAKDOWN_TASKS_QUERY_KEY = 'CompanyDashboardBreakdownTasks';

// ---- Dashboard Filters ----

const loadDashboardFilters = async (): Promise<DashboardFiltersResponse> => {
    const response = await createPacerApi()
        .get(`${API_ROOT}v2/company-dashboard/filters`)
        .json();
    const parsed = dashboardFiltersResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('loadDashboardFilters: response did not match schema', parsed.error);
        return dashboardFiltersResponseSchema.parse({});
    }
    return parsed.data;
};

export const useLoadDashboardFilters = (): UseSuspenseQueryResult<DashboardFiltersResponse> =>
    useSuspenseQuery<DashboardFiltersResponse>({
        queryKey: [DASHBOARD_FILTERS_QUERY_KEY],
        queryFn: loadDashboardFilters,
    });

// ---- Dashboard Settings ----

const loadDashboardSettings = async (): Promise<DashboardSettingsResponse> => {
    const response = await createPacerApi()
        .get(`${API_ROOT}v2/company-dashboard/settings`)
        .json();
    const parsed = dashboardSettingsResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('loadDashboardSettings: response did not match schema', parsed.error);
        return [];
    }
    return parsed.data;
};

export const useLoadDashboardSettings = (): UseSuspenseQueryResult<DashboardSettingsResponse> =>
    useSuspenseQuery<DashboardSettingsResponse>({
        queryKey: [DASHBOARD_SETTINGS_QUERY_KEY],
        queryFn: loadDashboardSettings,
    });

// ---- Overview Tabs Config ----

const getOverviewTabsConfig = async (): Promise<OverviewTabsConfigResponse> => {
    const response = await createPacerApi()
        .get(`${API_ROOT}v2/company-dashboard/overview-tabs-config`)
        .json();
    const parsed = overviewTabsConfigResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('getOverviewTabsConfig: response did not match schema', parsed.error);
        return overviewTabsConfigResponseSchema.parse({});
    }
    return parsed.data;
};

export const useGetOverviewTabsConfig = (): UseSuspenseQueryResult<OverviewTabsConfigResponse> =>
    useSuspenseQuery<OverviewTabsConfigResponse>({
        queryKey: [OVERVIEW_TABS_CONFIG_QUERY_KEY],
        queryFn: getOverviewTabsConfig,
    });

// ---- Mutations ----

// Save settings (POST — creates new)
const saveSettings = async (settings: DashboardSetting): Promise<SaveSettingsResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}v2/company-dashboard/settings`, { json: settings })
        .json();
    const parsed = saveSettingsResponseSchema.safeParse(response);
    return parsed.success ? parsed.data : (response as SaveSettingsResponse);
};

export const useSaveSettings = (
    onSuccess?: (data: SaveSettingsResponse) => void
): UseMutationResult<SaveSettingsResponse, Error, DashboardSetting> => {
    const queryClient = useQueryClient();
    return useMutation<SaveSettingsResponse, Error, DashboardSetting>({
        mutationFn: saveSettings,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: [DASHBOARD_SETTINGS_QUERY_KEY] });
            onSuccess?.(data);
        },
    });
};

// Update settings (PUT — updates existing by id)
const updateSettings = async ({ settings, id }: { settings: DashboardSetting; id: string }): Promise<SaveSettingsResponse> => {
    const response = await createPacerApi()
        .put(`${API_ROOT}v2/company-dashboard/settings/${id}`, { json: settings })
        .json();
    const parsed = saveSettingsResponseSchema.safeParse(response);
    return parsed.success ? parsed.data : (response as SaveSettingsResponse);
};

export const useUpdateSettings = (
    onSuccess?: (data: SaveSettingsResponse) => void
): UseMutationResult<SaveSettingsResponse, Error, { settings: DashboardSetting; id: string }> => {
    const queryClient = useQueryClient();
    return useMutation<SaveSettingsResponse, Error, { settings: DashboardSetting; id: string }>({
        mutationFn: updateSettings,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: [DASHBOARD_SETTINGS_QUERY_KEY] });
            onSuccess?.(data);
        },
    });
};

// Delete settings (DELETE — removes a saved view by id)
const deleteSettings = async (id: string): Promise<void> => {
    await createPacerApi()
        .delete(`${API_ROOT}v2/company-dashboard/settings/${id}`)
        .json();
};

export const useDeleteSettings = (
    onSuccess?: () => void
): UseMutationResult<void, Error, string> => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, string>({
        mutationFn: deleteSettings,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [DASHBOARD_SETTINGS_QUERY_KEY] });
            onSuccess?.();
        },
    });
};

// Check settings availability
const checkSettingsAvailability = async (settings: DashboardSetting): Promise<CheckSettingsAvailabilityResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}v2/company-dashboard/availability`, { json: settings })
        .json();
    const parsed = checkSettingsAvailabilityResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('checkSettingsAvailability: response did not match schema', parsed.error);
        return [];
    }
    return parsed.data;
};

export const useCheckSettingsAvailability = (): UseMutationResult<CheckSettingsAvailabilityResponse, Error, DashboardSetting> =>
    useMutation<CheckSettingsAvailabilityResponse, Error, DashboardSetting>({
        mutationFn: checkSettingsAvailability,
    });

// Update overview tabs config
const updateOverviewTabsConfig = async (config: UpdateTabsConfigRequest): Promise<void> => {
    await createPacerApi()
        .put(`${API_ROOT}v2/company-dashboard/overview-tabs-config`, { json: config })
        .json();
};

export const useUpdateOverviewTabsConfig = (
    onSuccess?: () => void
): UseMutationResult<void, Error, UpdateTabsConfigRequest> => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, UpdateTabsConfigRequest>({
        mutationFn: updateOverviewTabsConfig,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [OVERVIEW_TABS_CONFIG_QUERY_KEY] });
            onSuccess?.();
        },
    });
};

// Set active overview tab (fire-and-forget, no invalidation needed)
const setOverviewTab = async (request: SetOverviewTabRequest): Promise<void> => {
    await createPacerApi()
        .put(`${API_ROOT}v2/company-dashboard/set-overview-tab`, { json: request })
        .json();
};

export const useSetOverviewTab = (): UseMutationResult<void, Error, SetOverviewTabRequest> =>
    useMutation<void, Error, SetOverviewTabRequest>({
        mutationFn: setOverviewTab,
    });

// ---- Helper: build URLSearchParams from filters (handles arrays as repeated keys) ----

const buildSearchParams = (filters: OverviewLocationFilters): URLSearchParams => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
        if (value === undefined || value === null) continue;
        // Booleans are always sent explicitly (including `false`) so this GET stays consistent
        // with the sibling POST, which serializes the same filters object as JSON.
        if (Array.isArray(value)) {
            for (const item of value) {
                params.append(key, String(item));
            }
        } else {
            params.set(key, String(value));
        }
    }
    return params;
};

// ---- Helper: deterministic serialization of filters for query keys (sorted id arrays) ----

const serializeFilters = (filters: OverviewLocationFilters): string => {
    const normalized: Record<string, number[] | boolean> = {};
    for (const [key, value] of Object.entries(filters)) {
        normalized[key] = Array.isArray(value) ? [...value].sort((a, b) => a - b) : value;
    }
    return JSON.stringify(normalized, Object.keys(normalized).sort());
};

// ---- Load Locations By Projects ----

const EMPTY_LOCATIONS_BY_PROJECTS: LocationsByProjectsResponse = { brand: [], area: [], region: [], type: [], managementCompany: [] };

const loadLocationsByProjects = async (filters: OverviewLocationFilters): Promise<LocationsByProjectsResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}v2/company-dashboard/load-locations-by-project`, { json: filters })
        .json();
    const parsed = locationsByProjectsResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('loadLocationsByProjects: response did not match schema', parsed.error);
        return EMPTY_LOCATIONS_BY_PROJECTS;
    }
    return parsed.data;
};

export const useLoadLocationsByProjects = (
    filters: OverviewLocationFilters
): UseQueryResult<LocationsByProjectsResponse> =>
    useQuery<LocationsByProjectsResponse>({
        queryKey: [LOCATIONS_BY_PROJECTS_QUERY_KEY, serializeFilters(filters)],
        queryFn: () => loadLocationsByProjects(filters),
        initialData: EMPTY_LOCATIONS_BY_PROJECTS,
    });

// ---- Get Overview Tab ----

export interface OverviewTabSort {
    sortBy: SortByValue;
    sortDir: SortDirValue;
}

// The Locations widgets lazy-load: each row costs the API several drill-through lookups and a
// score calculation, so a tab resolving to hundreds of locations is fetched a page at a time.
export const OVERVIEW_TAB_PAGE_SIZE = 100;

const getOverviewTab = async (
    id: number,
    filters: OverviewLocationFilters,
    sort: OverviewTabSort,
    pageNumber: number
): Promise<OverviewTabResponse> => {
    const searchParams = buildSearchParams(filters);
    searchParams.set('sortBy', sort.sortBy);
    searchParams.set('sortDir', sort.sortDir);
    searchParams.set('pageSize', String(OVERVIEW_TAB_PAGE_SIZE));
    searchParams.set('pageNumber', String(pageNumber));
    const response = await createPacerApi()
        .get(`${API_ROOT}v2/company-dashboard/overview-tab/${id}`, {
            searchParams,
        })
        .json();
    const parsed = overviewTabResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('getOverviewTab: response did not match schema', parsed.error);
        return { locations: [], totalCount: 0 };
    }
    return parsed.data;
};

export const useGetOverviewTab = (
    id: number,
    filters: OverviewLocationFilters,
    sort: OverviewTabSort
): UseInfiniteQueryResult<InfiniteData<OverviewTabResponse>> =>
    useInfiniteQuery<OverviewTabResponse>({
        queryKey: [OVERVIEW_TAB_QUERY_KEY, id, serializeFilters(filters), sort.sortBy, sort.sortDir],
        queryFn: ({ pageParam }) => getOverviewTab(id, filters, sort, pageParam as number),
        initialPageParam: 1,
        // Driven off totalCount rather than a short last page, so a total that is an exact
        // multiple of the page size does not cost one extra, empty request. Counted by distinct id,
        // the way the list shows them: a row repeated across two pages is one location, and
        // counting it twice would stop one location short of the end.
        getNextPageParam: (lastPage, allPages) => {
            const fetched = new Set(allPages.flatMap((page) => page.locations.map((location) => location.id))).size;
            return lastPage.locations.length > 0 && fetched < lastPage.totalCount ? allPages.length + 1 : undefined;
        },
        // A refetch re-requests every page loaded so far, one after another, so a user who has
        // scrolled deep would pay for all of them on each return to the browser tab, and on each
        // quick hop away from Overview and back.
        refetchOnWindowFocus: false,
        staleTime: 30_000,
        // The list offers its own Retry when a page fails, so one quiet retry is enough before
        // handing the failure to the user, rather than the default three with backoff that keep
        // the loading row spinning for several seconds.
        retry: 1,
        // Deliberately no `initialData`: seeding it makes the query report `success` from the
        // first render, so `isPending` — and therefore `isLoading` — is false forever, leaving
        // callers with only `isFetching` (true on every background refetch) to drive a loading
        // state. Callers already tolerate `data` being undefined.
    });

// ---- Get Location Breakdown ----

const EMPTY_LOCATION_BREAKDOWN: LocationBreakdownResponse = { groupBy: '', groups: [] };

const getLocationBreakdown = async (
    locationKey: string,
    groupBy: string,
    tabId: number,
    filters: OverviewLocationFilters
): Promise<LocationBreakdownResponse> => {
    // Same flat filter params as overview-tab — the endpoint binds the identical FilterModel,
    // so the panel counts exactly the tasks the location row above it counts.
    const searchParams = buildSearchParams(filters);
    searchParams.set('locationKey', locationKey);
    searchParams.set('groupBy', groupBy);
    searchParams.set('tabId', String(tabId));

    const response = await createPacerApi()
        .get(`${API_ROOT}v2/company-dashboard/overview-location-breakdown`, { searchParams })
        .json();
    const parsed = locationBreakdownResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('getLocationBreakdown: response did not match schema', parsed.error);
        return EMPTY_LOCATION_BREAKDOWN;
    }
    return parsed.data;
};

/**
 * One location's metrics broken down by department / discipline / phase / tag.
 *
 * `enabled` is what keeps the breakdown panel honest: it is passed the panel's expanded
 * state, so a panel that is animating shut (still mounted, no longer expanded) does not
 * fire a fetch, and a row that was never opened never costs a request.
 */
export const useGetLocationBreakdown = (
    locationKey: string | null,
    groupBy: string,
    tabId: number,
    filters: OverviewLocationFilters,
    enabled = true
): UseQueryResult<LocationBreakdownResponse> =>
    useQuery<LocationBreakdownResponse>({
        queryKey: [LOCATION_BREAKDOWN_QUERY_KEY, locationKey, groupBy, tabId, serializeFilters(filters)],
        queryFn: () => getLocationBreakdown(locationKey as string, groupBy, tabId, filters),
        enabled: enabled && !!locationKey,
    });

// ---- Get Breakdown Task List ----

/**
 * How many tasks one drill-down asks for. The sidebar scrolls rather than pages, so this is
 * sized to cover the overwhelming majority of groups in one request; anything past it is
 * reported as a count rather than silently dropped.
 */
export const DEFAULT_BREAKDOWN_TASKS_PAGE_SIZE = 100;

const EMPTY_BREAKDOWN_TASKS: BreakdownTaskListResponse = { results: [], totalCount: 0, columnOrder: [] };

export interface BreakdownTasksParams {
    locationKey: string;
    /** The breakdown view the row came from — the endpoint's `groupBy`. */
    groupBy: string;
    groupId: number;
    /** Which clicked value to list. See BreakdownMetric in Openings/breakdownSelection.ts. */
    metric: string;
    tabId: number;
    filters: OverviewLocationFilters;
    pageNumber?: number;
    pageSize?: number;
}

const getBreakdownTasks = async (params: BreakdownTasksParams): Promise<BreakdownTaskListResponse> => {
    // The same flat filter params the breakdown panel sends, because the endpoint binds the
    // identical FilterModel — that is what makes the list length agree with the number clicked.
    const searchParams = buildSearchParams(params.filters);
    searchParams.set('locationKey', params.locationKey);
    searchParams.set('groupBy', params.groupBy);
    searchParams.set('groupId', String(params.groupId));
    searchParams.set('metric', params.metric);
    searchParams.set('tabId', String(params.tabId));
    // Paging rides in on the filter model. Always sent: the server defaults to 10 rows a page,
    // which would silently truncate a sidebar list that has no pager of its own.
    searchParams.set('pageNumber', String(params.pageNumber ?? 1));
    searchParams.set('pageSize', String(params.pageSize ?? DEFAULT_BREAKDOWN_TASKS_PAGE_SIZE));

    const response = await createPacerApi()
        .get(`${API_ROOT}v2/company-dashboard/overview-breakdown-tasks`, { searchParams })
        .json();
    const parsed = breakdownTaskListResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('getBreakdownTasks: response did not match schema', parsed.error);
        return EMPTY_BREAKDOWN_TASKS;
    }
    return parsed.data;
};

/**
 * The tasks behind one clicked breakdown value, for the sidebar's Tasks tab.
 *
 * `enabled` is false whenever there is nothing to drill into (no group selected), so simply
 * opening the Tasks tab on a plain location row costs no request.
 */
export const useGetBreakdownTasks = (
    params: BreakdownTasksParams | null,
    enabled = true
): UseQueryResult<BreakdownTaskListResponse> =>
    useQuery<BreakdownTaskListResponse>({
        queryKey: [
            BREAKDOWN_TASKS_QUERY_KEY,
            params?.locationKey ?? null,
            params?.groupBy ?? null,
            params?.groupId ?? null,
            params?.metric ?? null,
            params?.tabId ?? null,
            params?.pageNumber ?? 1,
            params?.pageSize ?? DEFAULT_BREAKDOWN_TASKS_PAGE_SIZE,
            params ? serializeFilters(params.filters) : null,
        ],
        queryFn: () => getBreakdownTasks(params as BreakdownTasksParams),
        enabled: enabled && params !== null,
    });
